import { SelectionPackaged } from "@screencloud/screencloud-ui-components";
import { useEffect } from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import type { StepProps } from "../../hooks/usePixiWizardState";
import { ExclusiveDeals } from "./ExclusiveDeals";
import { PlanCard } from "./PlanCard";
import {
  BillingCycle,
  DealId,
  dealEligibility,
  DEFAULT_BILLING_CYCLE,
  DEFAULT_PLAN,
  PLAN_CONTENT,
  PlanId,
} from "./constants";
import { resolveSelection } from "./selection";
import { useOnboardingPlansContext } from "./useOnboardingPlans";
import {
  CardsRow,
  ChoiceColumn,
  ChoiceHeader,
  ChoiceTitle,
  PlanStateMessage,
  SelectorsRow,
  StepLayout,
} from "./styles";

const PlanBillingStepComponent = ({
  state,
  updateState,
  intl,
}: StepProps & InjectedIntlProps) => {
  const { loading, error, getPlan, isProTrialist } =
    useOnboardingPlansContext();

  const { planId, currency, billingCycle, shippingCountry, licenses } =
    resolveSelection(state, isProTrialist);

  // Persist the default selection on mount so the highlighted card and the
  // Continue gate agree (Pro-trialists are pinned to Pro). Free-device is the
  // default deal whenever deals are available (Yearly, either plan).
  useEffect(() => {
    const patch: Partial<typeof state> = {};
    const nextPlan = isProTrialist ? "pro" : (state.planId ?? DEFAULT_PLAN);
    const nextCycle = state.billingCycle ?? DEFAULT_BILLING_CYCLE;
    if (!state.planId) patch.planId = nextPlan;
    if (!state.billingCycle) patch.billingCycle = nextCycle;
    if (!state.dealId && dealEligibility(nextCycle).freeDeviceAvailable) {
      patch.dealId = "free-device";
    }
    if (Object.keys(patch).length) updateState(patch);
  }, []);

  // Both plans offer the same deals now, so switching plan keeps the deal.
  const selectPlan = (next: PlanId) => {
    if (next === planId) return;
    updateState({ planId: next });
  };

  const selectBillingCycle = (cycle: BillingCycle) =>
    updateState({
      billingCycle: cycle,
      // Monthly has no deals — drop any deal; Yearly defaults to free-device.
      dealId: cycle === "month" ? undefined : (state.dealId ?? "free-device"),
    });

  // Pro-trialist: each card is a cycle; selecting it sets pro + that cycle.
  const selectProCycle = (cycle: BillingCycle) =>
    updateState({
      planId: "pro",
      billingCycle: cycle,
      dealId: cycle === "month" ? undefined : (state.dealId ?? "free-device"),
    });

  const billingOptions = [
    {
      label: intl.formatMessage({
        id: "pixi_wizard.plan.billing.yearly",
        defaultMessage: "Yearly",
      }),
      value: "year",
    },
    {
      label: intl.formatMessage({
        id: "pixi_wizard.plan.billing.monthly",
        defaultMessage: "Monthly",
      }),
      value: "month",
    },
  ];

  const monthlySuffix = intl.formatMessage({
    id: "pixi_wizard.plan.cycle.monthly",
    defaultMessage: "Monthly",
  });
  const annualSuffix = intl.formatMessage({
    id: "pixi_wizard.plan.cycle.annual",
    defaultMessage: "Annual",
  });

  const corePlan = getPlan("core", billingCycle, currency);
  const proPlan = getPlan("pro", billingCycle, currency);
  const coreMonthly = getPlan("core", "month", currency);
  const proMonthly = getPlan("pro", "month", currency);
  const proAnnual = getPlan("pro", "year", currency);

  // Yearly + free-device shows the monthly rate instead of yearly/12 — the free
  // Pixi replaces the annual discount (GRWBKK-1695). See `billingPeriodPriceCents`.
  const isFreeDevice = state.dealId === "free-device";

  const hasPlans = isProTrialist
    ? Boolean(proMonthly || proAnnual)
    : Boolean(corePlan || proPlan);

  const renderCards = () => {
    if (loading) {
      return (
        <PlanStateMessage data-testid="plan-billing-loading">
          <FormattedMessage
            id="pixi_wizard.plan.loading"
            defaultMessage="Loading plans…"
          />
        </PlanStateMessage>
      );
    }
    if (error) {
      return (
        <PlanStateMessage data-testid="plan-billing-error">
          <FormattedMessage
            id="pixi_wizard.plan.error"
            defaultMessage="Couldn't load plans. Please try again."
          />
        </PlanStateMessage>
      );
    }
    if (!hasPlans) {
      return (
        <PlanStateMessage data-testid="plan-billing-empty">
          <FormattedMessage
            id="pixi_wizard.plan.empty"
            defaultMessage="Plans are unavailable right now. Please try again later."
          />
        </PlanStateMessage>
      );
    }

    if (isProTrialist) {
      return (
        <CardsRow>
          <PlanCard
            content={PLAN_CONTENT.pro}
            plan={proMonthly}
            billingCycle="month"
            cycleSuffix={monthlySuffix}
            selected={billingCycle === "month"}
            onSelect={() => selectProCycle("month")}
            data-testid="plan-card-pro-monthly"
          />
          <PlanCard
            content={PLAN_CONTENT.pro}
            plan={proAnnual}
            monthlyPlan={proMonthly}
            isFreeDevice={isFreeDevice}
            billingCycle="year"
            cycleSuffix={annualSuffix}
            selected={billingCycle === "year"}
            onSelect={() => selectProCycle("year")}
            data-testid="plan-card-pro-annual"
          />
        </CardsRow>
      );
    }

    return (
      <CardsRow>
        <PlanCard
          content={PLAN_CONTENT.core}
          plan={corePlan}
          monthlyPlan={coreMonthly}
          isFreeDevice={isFreeDevice}
          billingCycle={billingCycle}
          selected={planId === "core"}
          onSelect={() => selectPlan("core")}
          data-testid="plan-card-core"
        />
        <PlanCard
          content={PLAN_CONTENT.pro}
          plan={proPlan}
          monthlyPlan={proMonthly}
          isFreeDevice={isFreeDevice}
          billingCycle={billingCycle}
          selected={planId === "pro"}
          onSelect={() => selectPlan("pro")}
          data-testid="plan-card-pro"
        />
      </CardsRow>
    );
  };

  return (
    <StepLayout data-testid="plan-billing-step">
      <ChoiceColumn>
        <ChoiceHeader>
          <ChoiceTitle data-testid="plan-billing-title">
            <FormattedMessage
              id="pixi_wizard.plan.title"
              defaultMessage="Choose your plan"
            />
          </ChoiceTitle>
          {/* Currency is the trialist's own (Chargebee locks it) — not shown as
              a selector; it's already visible in the plan prices. Pro-trialists
              have no billing toggle either, so the row is omitted (the remaining
              Billing toggle stays centred by SelectorsRow). */}
          {!isProTrialist && (
            <SelectorsRow>
              <SelectionPackaged
                data-testid="billing-selector"
                label={intl.formatMessage({
                  id: "pixi_wizard.plan.billing_label",
                  defaultMessage: "Billing",
                })}
                options={billingOptions}
                value={billingCycle}
                onChange={(value) => selectBillingCycle(value as BillingCycle)}
              />
            </SelectorsRow>
          )}
        </ChoiceHeader>

        {renderCards()}
      </ChoiceColumn>

      <ExclusiveDeals
        billingCycle={billingCycle}
        currency={currency}
        licenses={licenses}
        selectedDealId={state.dealId}
        shippingCountry={shippingCountry}
        onSelectDeal={(dealId: DealId) => updateState({ dealId })}
        onChangeShippingCountry={(value) =>
          updateState({ shippingCountry: value })
        }
      />
    </StepLayout>
  );
};

export const PlanBillingStep = injectIntl(PlanBillingStepComponent);
